import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  AppBar,
  Toolbar,
  InputBase,
  alpha,
  styled,
  TablePagination,
  Avatar,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  AccessTime as AttendanceIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { getdata, updateStudentAttendance, bulkUpdateStudentAttendance } from './redux/students';
import { schooldetails } from './redux/schooldetails';
import axios from 'axios';
import { getStudentThumbnailUrl, getStudentFullImageUrl } from './utils/studentPhotoUrl';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

export default function StudentAttendance() {
  const dispatch = useDispatch();
  const allStudents = useSelector((state) => state.students) || [];
  const admin = useSelector((state) => state.admin) || {};
  const schooldetails_ = useSelector((state) => state.schooldetails);
  const isTeacher = ["Class Teacher", "Class and Subject Teacher", "Subject Teacher"].includes(admin.positiondescription);

  const students = useMemo(() => {
    if (!isTeacher) return allStudents;
    const teacherClassesRaw = Array.isArray(admin.class_) ? admin.class_.flat() : [];
    const teacherClasses = teacherClassesRaw.filter(Boolean);
    if (teacherClasses.length !== 0) {
      return allStudents.filter((student) => teacherClasses.includes(student.class_));
    }
    if (admin.positiondescription_) {
      return allStudents.filter((student) => student.class_ === admin.positiondescription_);
    }
    return [];
  }, [allStudents, isTeacher, admin.class_, admin.positiondescription_]);

  // State management
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStudents, setFilteredStudents] = useState(students);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [attendanceStats, setAttendanceStats] = useState({
    total: 0,
    present: 0,
    absent: 0,
    percentage: 0
  });

  // Analytics and Reports State
  const [activeTab, setActiveTab] = useState(0);
  const [analyticsData, setAnalyticsData] = useState({
    dailyStats: [],
    classStats: {},
    termStats: {},
    weeklyTrend: [],
    monthlyTrend: [],
    topAbsentees: [],
    attendancePatterns: {}
  });
  const [reportDateRange, setReportDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [selectedReportTerm, setSelectedReportTerm] = useState('');
  const [showReportsDialog, setShowReportsDialog] = useState(false);

  // Get current date and time
  const dt = new Date();
  const time = dt.getHours() + ":" + dt.getMinutes() + ":" + dt.getSeconds();

  // Get current term from school details (like sis-for-teachers pattern)
  const getCurrentTerm = useCallback(() => {
    if (schooldetails_ && schooldetails_.length > 0 && schooldetails_[0].terms && schooldetails_[0].terms.length > 0) {
      return schooldetails_[0].terms[schooldetails_[0].terms.length - 1];
    }
    return '';
  }, [schooldetails_]);

  // Load students data
  const loadStudents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post("https://servermain.sisrevolution.com/api/students/getstudentdata/", {
        schoolID: admin.schoolID
      });
      
      if (response.status === 200) {
        // DO NOT update Redux with sorted response - keep original order
        setFilteredStudents(response.data);
        calculateStats(response.data);
      }
    } catch (error) {
      showMessage('Error loading students data', 'error');
    } finally {
      setLoading(false);
    }
  }, [admin.schoolID, dispatch]);

  // Calculate attendance statistics - Use ALL students, not filtered ones
  const calculateStats = useCallback((studentList = students) => {
    const total = studentList.length;
    let present = 0;
    let absent = 0;

    studentList.forEach(student => {
      if (student.attendance && student.attendance[selectedTerm]) {
        const todayAttendance = student.attendance[selectedTerm].find(
          att => att.date === selectedDate
        );
        if (todayAttendance) {
          if (todayAttendance.attendance === 'present') present++;
          else if (todayAttendance.attendance === 'absent') absent++;
        }
      }
    });

    const percentage = total > 0 ? Math.round((present / total) * 100) : 0;

    setAttendanceStats({
      total,
      present,
      absent,
      percentage
    });
  }, [selectedDate, selectedTerm, students]);

  // Calculate comprehensive analytics from actual attendance data
  const calculateAnalytics = useCallback(() => {
    if (!students || students.length === 0) {
      setAnalyticsData({
        dailyStats: [],
        classStats: {},
        termStats: {},
        weeklyTrend: [],
        monthlyTrend: [],
        topAbsentees: [],
        attendancePatterns: {}
      });
      return;
    }

    const currentTerm = getCurrentTerm();
    if (!currentTerm) {
      setAnalyticsData({
        dailyStats: [],
        classStats: {},
        termStats: {},
        weeklyTrend: [],
        monthlyTrend: [],
        topAbsentees: [],
        attendancePatterns: {}
      });
      return;
    }

    // Daily Statistics for the last 30 days
    const dailyStats = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      let present = 0;
      let absent = 0;
      let unmarked = 0;

      students.forEach(student => {
        if (student.attendance && student.attendance[currentTerm]) {
          const dayAttendance = student.attendance[currentTerm].find(
            att => att.date === dateStr
          );
          if (dayAttendance) {
            if (dayAttendance.attendance === 'present') present++;
            else if (dayAttendance.attendance === 'absent') absent++;
          } else {
            unmarked++;
          }
        } else {
          unmarked++;
        }
      });

      dailyStats.push({
        date: dateStr,
        present,
        absent,
        unmarked,
        total: students.length,
        percentage: students.length > 0 ? Math.round((present / students.length) * 100) : 0
      });
    }

    // Class-wise Statistics
    const classStats = {};
    const classes = [...new Set(students.map(s => s.class_))];
    
    classes.forEach(className => {
      const classStudents = students.filter(s => s.class_ === className);
      let present = 0;
      let absent = 0;
      let unmarked = 0;

      classStudents.forEach(student => {
        if (student.attendance && student.attendance[currentTerm]) {
          const todayAttendance = student.attendance[currentTerm].find(
            att => att.date === selectedDate
          );
          if (todayAttendance) {
            if (todayAttendance.attendance === 'present') present++;
            else if (todayAttendance.attendance === 'absent') absent++;
          } else {
            unmarked++;
          }
        } else {
          unmarked++;
        }
      });

      classStats[className] = {
        total: classStudents.length,
        present,
        absent,
        unmarked,
        percentage: classStudents.length > 0 ? Math.round((present / classStudents.length) * 100) : 0
      };
    });

    // Term-wise Statistics (if multiple terms exist)
    const termStats = {};
    if (schooldetails_ && schooldetails_.length > 0 && schooldetails_[0].terms) {
      schooldetails_[0].terms.forEach(term => {
        let present = 0;
        let absent = 0;
        let totalDays = 0;

        students.forEach(student => {
          if (student.attendance && student.attendance[term]) {
            totalDays = Math.max(totalDays, student.attendance[term].length);
            student.attendance[term].forEach(att => {
              if (att.attendance === 'present') present++;
              else if (att.attendance === 'absent') absent++;
            });
          }
        });

        termStats[term] = {
          totalDays,
          present,
          absent,
          totalRecords: present + absent,
          percentage: (present + absent) > 0 ? Math.round((present / (present + absent)) * 100) : 0
        };
      });
    }

    // Weekly Trend (last 4 weeks)
    const weeklyTrend = [];
    for (let week = 3; week >= 0; week--) {
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - (week * 7 + 6));
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() - (week * 7));

      let weekPresent = 0;
      let weekAbsent = 0;
      let weekDays = 0;

      for (let d = 0; d < 7; d++) {
        const date = new Date(weekStart);
        date.setDate(date.getDate() + d);
        const dateStr = date.toISOString().split('T')[0];

        students.forEach(student => {
          if (student.attendance && student.attendance[currentTerm]) {
            const dayAttendance = student.attendance[currentTerm].find(
              att => att.date === dateStr
            );
            if (dayAttendance) {
              weekDays++;
              if (dayAttendance.attendance === 'present') weekPresent++;
              else if (dayAttendance.attendance === 'absent') weekAbsent++;
            }
          }
        });
      }

      weeklyTrend.push({
        week: `Week ${4 - week}`,
        present: weekPresent,
        absent: weekAbsent,
        days: weekDays,
        percentage: weekDays > 0 ? Math.round((weekPresent / weekDays) * 100) : 0
      });
    }

    // Top Absentees (students with most absences in current term)
    const topAbsentees = students.map(student => {
      let absences = 0;
      if (student.attendance && student.attendance[currentTerm]) {
        absences = student.attendance[currentTerm].filter(
          att => att.attendance === 'absent'
        ).length;
      }
      return {
        ...student,
        absences,
        attendanceRate: student.attendance && student.attendance[currentTerm] 
          ? student.attendance[currentTerm].length > 0 
            ? Math.round(((student.attendance[currentTerm].filter(att => att.attendance === 'present').length) / student.attendance[currentTerm].length) * 100)
            : 0
          : 0
      };
    }).sort((a, b) => b.absences - a.absences).slice(0, 10);

    // Attendance Patterns (by day of week)
    const attendancePatterns = {
      Monday: { present: 0, absent: 0 },
      Tuesday: { present: 0, absent: 0 },
      Wednesday: { present: 0, absent: 0 },
      Thursday: { present: 0, absent: 0 },
      Friday: { present: 0, absent: 0 },
      Saturday: { present: 0, absent: 0 },
      Sunday: { present: 0, absent: 0 }
    };

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    students.forEach(student => {
      if (student.attendance && student.attendance[currentTerm]) {
        student.attendance[currentTerm].forEach(att => {
          // Validate date before processing
          if (att.date) {
            const date = new Date(att.date);
            // Check if date is valid
            if (!isNaN(date.getTime())) {
              const dayName = dayNames[date.getDay()];
              
              // Ensure the day exists in attendancePatterns before accessing
              if (dayName && attendancePatterns[dayName]) {
                if (att.attendance === 'present') {
                  attendancePatterns[dayName].present++;
                } else if (att.attendance === 'absent') {
                  attendancePatterns[dayName].absent++;
                }
              }
            }
          }
        });
      }
    });

    setAnalyticsData({
      dailyStats,
      classStats,
      termStats,
      weeklyTrend,
      monthlyTrend: dailyStats, // Using daily stats as monthly for now
      topAbsentees,
      attendancePatterns
    });
  }, [students, selectedDate, selectedTerm, getCurrentTerm, schooldetails_]);

  // Filter students based on search term and class - FOR DISPLAY ONLY
  const filterStudents = useCallback(() => {
    let filtered = students;

    if (selectedClass) {
      filtered = filtered.filter(student => student.class_ === selectedClass);
    }

    if (searchTerm) {
      filtered = filtered.filter(student => 
        student.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.othernames.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.ID.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredStudents(filtered);
    // Calculate stats for ALL students, not just filtered ones
    calculateStats(students);
    setSelectedStudents([]);
    setSelectAll(false);
  }, [students, selectedClass, searchTerm, calculateStats]);

  // Mark individual student attendance - EFFICIENT Redux Update (NO CLASS SORTING)
  const markAttendance = async (data, status) => {
    const currentTerm = getCurrentTerm();
    
    if (!currentTerm) {
      showMessage("No current term found. Please check school settings.", "error");
      return;
    }

    // Check if attendance already exists for this date
    const existingAttendance = data.attendance && data.attendance[currentTerm] 
      ? data.attendance[currentTerm].find(att => att.date === selectedDate)
      : null;

    // If attendance exists and is already the same status, show error
    if (existingAttendance && existingAttendance.attendance === status) {
      showMessage(`Student is already marked ${status} for this date`, "error");
      return;
    }

    const attendanceRecord = {
      date: selectedDate,
      time: time,
      attendance: status
    };

    try {
      // Update Redux state immediately - EFFICIENT APPROACH (NO CLASS SORTING)
      dispatch(updateStudentAttendance({
        studentId: data._id,
        attendanceData: attendanceRecord,
        term: currentTerm,
        date: selectedDate
      }));

      // Show success message immediately
      showMessage(`Student marked as ${status}`, 'success');

      // Make API call in background - DO NOT UPDATE REDUX WITH SORTED RESPONSE
      const apiData = {
        id: data._id,
        class: data.class_,
        data: { ...data.attendance, [currentTerm]: [...(data.attendance[currentTerm] || []), attendanceRecord] },
        term: currentTerm,
        schoolID: admin.schoolID
      };

      // Add SMS data for absent students
      if (status === "absent") {
        apiData.type = "absent";
        apiData.info = data;
        apiData.smsname = admin.smsname || 'School';
        apiData.smsID = admin.smsID || '';
      }

      await axios.post("https://servermain.sisrevolution.com/api/students/markattendance/", apiData)
        .then(res => {
          if (res.status === 200) {
            // DO NOT update Redux with sorted response - keep original order
            // The Redux state is already updated with the correct attendance data
            if (status === "absent") {
              showMessage("Guardian Notification Sent", "success");
            }
          }
        });

      // Recalculate stats for ALL students after successful update
      setTimeout(() => {
        calculateStats(students);
      }, 100);

    } catch (err) {
      // Revert Redux state on error
      loadStudents();
      showMessage("Network Error", "error");
    }
  };

  // Handle individual student selection
  const handleStudentSelect = (studentId) => {
    const newSelected = selectedStudents.includes(studentId)
      ? selectedStudents.filter(id => id !== studentId)
      : [...selectedStudents, studentId];
    
    setSelectedStudents(newSelected);
    setSelectAll(newSelected.length === filteredStudents.length);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(student => student._id));
    }
    setSelectAll(!selectAll);
  };

  // Apply bulk action - EFFICIENT Redux Update (NO CLASS SORTING)
  const applyBulkAction = async () => {
    if (!bulkAction || selectedStudents.length === 0) return;

    const currentTerm = getCurrentTerm();
    if (!currentTerm) {
      showMessage("No current term found. Please check school settings.", "error");
      return;
    }

    // setLoading(true);
    
    try {
      // ===== PHASE 1: IMMEDIATE REDUX UPDATES =====
      
      // Batch Preparation: Creates an array of attendance updates for all selected students
      const attendanceUpdates = selectedStudents.map(studentId => ({
        studentId: studentId,
        attendanceData: {
          date: selectedDate,
          time: time,
          attendance: bulkAction
        },
        term: currentTerm,
        date: selectedDate
      }));

      // Bulk Redux Dispatch: Uses bulkUpdateStudentAttendance to update Redux state for all students at once
      dispatch(bulkUpdateStudentAttendance({ attendanceUpdates }));

      // Instant UI Feedback: Shows success message immediately for all students
      showMessage(`${selectedStudents.length} students marked as ${bulkAction}`, 'success');
      
      // Dialog Cleanup: Closes bulk dialog and clears selections
      setShowBulkDialog(false);
      setSelectedStudents([]);
      setSelectAll(false);

      // ===== PHASE 2: BACKGROUND API PROCESSING =====
      
      // Parallel Processing: Creates individual API calls for each selected student
      const apiPromises = selectedStudents.map(async (studentId) => {
        const student = students.find(s => s._id === studentId);
        if (!student) {
          throw new Error(`Student with ID ${studentId} not found`);
        }

        const attendanceRecord = {
          date: selectedDate,
          time: time,
          attendance: bulkAction
        };

        // Deep Copy Creation: For each student, creates a deep copy of their attendance data
        let updatedAttendance = {};
        if (student.attendance) {
          Object.keys(student.attendance).forEach(term => {
            updatedAttendance[term] = [...student.attendance[term]];
          });
        }
        
        // Initialize current term if it doesn't exist
        if (!updatedAttendance[currentTerm]) {
          updatedAttendance[currentTerm] = [];
        }

        // Attendance Logic: For each student
        const existingIndex = updatedAttendance[currentTerm].findIndex(
          att => att.date === selectedDate
        );

        if (existingIndex >= 0) {
          // Updates existing record
          updatedAttendance[currentTerm][existingIndex] = attendanceRecord;
        } else {
          // Adds new record
          updatedAttendance[currentTerm].push(attendanceRecord);
        }

        // Prepares API data with updated attendance
        const apiData = {
          id: studentId,
          class: student.class_,
          data: updatedAttendance,
          term: currentTerm,
          schoolID: admin.schoolID
        };

        // SMS Integration: If marking as absent, adds SMS data for guardian notifications
        if (bulkAction === "absent") {
          apiData.type = "absent";
          apiData.info = student;
          apiData.smsname = admin.smsname || 'School';
          apiData.smsID = admin.smsID || '';
        }

        // Make individual API call
        return axios.post("https://servermain.sisrevolution.com/api/students/markattendance/", apiData);
      });

      // Concurrent Execution: All API calls run in parallel using Promise.all()
      await Promise.all(apiPromises);
      
      // Show additional success message for SMS notifications if applicable
      if (bulkAction === "absent") {
        showMessage("Guardian notifications sent for absent students", "success");
      }
      
      // Recalculate stats for ALL students after successful update
      setTimeout(() => {
        calculateStats(students);
      }, 100);
      
    } catch (error) {
      // Error Handling: If any API call fails, the entire operation is reverted
      console.error('Bulk attendance error:', error);
      loadStudents(); // Revert Redux state by reloading students data
      showMessage(`Error in bulk operation: ${error.message || 'Network error'}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Get attendance status for a student on selected date
  const getAttendanceStatus = (student) => {
    const currentTerm = getCurrentTerm();
    if (!student.attendance || !student.attendance[currentTerm]) return 'unmarked';
    
    const todayAttendance = student.attendance[currentTerm].find(
      att => att.date === selectedDate
    );
    
    return todayAttendance ? todayAttendance.attendance : 'unmarked';
  };

  // Show message
  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setOpenSnackbar(true);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Export attendance data
  const exportAttendanceData = () => {
    const currentTerm = getCurrentTerm();
    if (!currentTerm) {
      showMessage("No current term found", "error");
      return;
    }

    const csvData = [];
    csvData.push(['Student ID', 'Name', 'Class', 'Date', 'Status', 'Time']);

    students.forEach(student => {
      if (student.attendance && student.attendance[currentTerm]) {
        student.attendance[currentTerm].forEach(att => {
          csvData.push([
            student.ID,
            `${student.lastname} ${student.othernames}`,
            student.class_,
            att.date,
            att.attendance,
            att.time
          ]);
        });
      }
    });

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `attendance_report_${currentTerm}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    showMessage("Attendance data exported successfully", "success");
  };

  // Get paginated students
  const getPaginatedStudents = () => {
    const startIndex = page * rowsPerPage;
    return filteredStudents.slice(startIndex, startIndex + rowsPerPage);
  };

  // Load data on component mount
  useEffect(() => {
    // loadStudents();
  }, [loadStudents]);

  // Set current term automatically when school details are loaded
  useEffect(() => {
    const currentTerm = getCurrentTerm();
    if (currentTerm) {
      setSelectedTerm(currentTerm);
    }
  }, [getCurrentTerm]);

  // Filter students when search term or class changes
  useEffect(() => {
    filterStudents();
  }, [filterStudents]);

  // Recalculate stats when students data changes (for all students, not filtered)
  useEffect(() => {
    calculateStats(students);
    calculateAnalytics();
  }, [students, selectedDate, selectedTerm, calculateStats, calculateAnalytics]);

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <AppBar position="static" sx={{ mb: 3, background: 'linear-gradient(315deg, #17dddd 0%, #1760d3 74%)' }}>
        <Toolbar>
          <AttendanceIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Student Attendance Management
          </Typography>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search students..."
              inputProps={{ 'aria-label': 'search' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Search>
        </Toolbar>
      </AppBar>

      {/* Current Term Display */}
      {getCurrentTerm() && (
        <Paper sx={{ p: 2, mb: 3, background: 'linear-gradient(315deg, #e3f2fd 0%, #f3e5f5 100%)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" color="primary" gutterBottom>
                Current Term: {getCurrentTerm()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Attendance will be automatically recorded for the current term
              </Typography>
            </Box>
            <Chip
              label="AUTO"
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>
        </Paper>
      )}

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={0} variant="fullWidth">
          <Tab 
            icon={<AttendanceIcon />} 
            label="Attendance Management" 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Statistics Cards - Shows stats for ALL students, not just filtered ones */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Students
              </Typography>
              <Typography variant="h4">
                {attendanceStats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Present
              </Typography>
              <Typography variant="h4" color="success.main">
                {attendanceStats.present}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Absent
              </Typography>
              <Typography variant="h4" color="error.main">
                {attendanceStats.absent}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Attendance %
              </Typography>
              <Typography variant="h4" color="primary.main">
                {attendanceStats.percentage}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Class (Display Filter)</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <MenuItem value="">All Classes</MenuItem>
                {Array.from(new Set(students.map(s => s.class_))).map(cls => (
                  <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {/* <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Term (Auto-detected)</InputLabel>
              <Select
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
                disabled={!!getCurrentTerm()}
              >
                <MenuItem value="">Select Term</MenuItem>
                <MenuItem value="1st Term">1st Term</MenuItem>
                <MenuItem value="2nd Term">2nd Term</MenuItem>
                <MenuItem value="3rd Term">3rd Term</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}
          <Grid item xs={12} sm={5}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => setShowBulkDialog(true)}
                disabled={selectedStudents.length === 0}
                color="primary"
              >
                Bulk Actions ({selectedStudents.length})
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadStudents}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Students Table */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            Students ({filteredStudents.length})
            {selectedStudents.length > 0 && (
              <Chip
                label={`${selectedStudents.length} selected`}
                color="primary"
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedStudents.length > 0 && selectedStudents.length < filteredStudents.length}
                        checked={selectAll && filteredStudents.length > 0}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Photo</TableCell>
                    <TableCell>Student ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getPaginatedStudents().map((student) => {
                    const status = getAttendanceStatus(student);
                    const isSelected = selectedStudents.includes(student._id);
                    
                    return (
                      <TableRow
                        key={student._id}
                        selected={isSelected}
                        hover
                        sx={{ 
                          '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleStudentSelect(student._id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Avatar
                            src={getStudentThumbnailUrl(student) || undefined}
                            alt={student.lastname}
                            sx={{ width: 40, height: 40 }}
                            imgProps={{
                              onError: (e) => {
                                const full = getStudentFullImageUrl(student);
                                if (full && e.currentTarget.src !== full) {
                                  e.currentTarget.src = full;
                                  return;
                                }
                                e.currentTarget.removeAttribute('src');
                              }
                            }}
                          >
                            <PersonIcon />
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {student.ID}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {student.lastname.toUpperCase()} {student.othernames.toUpperCase()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={student.class_}
                            size="small"
                            color="default"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={status.toUpperCase()}
                            color={
                              status === 'present' ? 'success' :
                              status === 'absent' ? 'error' : 'default'
                            }
                            variant={status === 'unmarked' ? 'outlined' : 'filled'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Mark Present">
                              <IconButton
                                color="success"
                                onClick={() => markAttendance(student, 'present')}
                                disabled={status === 'present'}
                                size="small"
                              >
                                <PresentIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark Absent">
                              <IconButton
                                color="error"
                                onClick={() => markAttendance(student, 'absent')}
                                disabled={status === 'absent'}
                                size="small"
                              >
                                <AbsentIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredStudents.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Bulk Actions Dialog */}
      <Dialog open={showBulkDialog} onClose={() => setShowBulkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Bulk Attendance Actions
          <Typography variant="body2" color="textSecondary">
            {selectedStudents.length} students selected
          </Typography>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
            >
              <MenuItem value="present">Mark All Present</MenuItem>
              <MenuItem value="absent">Mark All Absent</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBulkDialog(false)}>Cancel</Button>
          <Button 
            onClick={applyBulkAction} 
            variant="contained"
            disabled={!bulkAction || selectedStudents.length === 0}
          >
            Apply to {selectedStudents.length} students
          </Button>
        </DialogActions>
      </Dialog>

          {/* Snackbar for messages */}
          <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
          >
            <Alert onClose={handleSnackbarClose} severity={messageType}>
              {message}
            </Alert>
          </Snackbar>
        </>
      )}

      {/* Analytics & Reports Tab - removed per requirement */}
      {false && (
        <Box>
          {/* Analytics Header */}
          <Paper sx={{ p: 2, mb: 3, background: 'linear-gradient(315deg, #f3e5f5 0%, #e8f5e8 100%)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h5" color="primary" gutterBottom>
                  <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Attendance Analytics & Reports
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Comprehensive insights based on actual attendance data
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={exportAttendanceData}
                color="primary"
              >
                Export Data
              </Button>
            </Box>
          </Paper>

          {/* Analytics Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BarChartIcon color="primary" sx={{ mr: 1 }} />
                    <Typography color="textSecondary" gutterBottom>
                      Average Daily Attendance
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="primary.main">
                    {(analyticsData.dailyStats || []).length > 0 
                      ? Math.round((analyticsData.dailyStats || []).reduce((sum, day) => sum + (day?.percentage || 0), 0) / (analyticsData.dailyStats || []).length)
                      : 0}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Last 30 days
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SchoolIcon color="success" sx={{ mr: 1 }} />
                    <Typography color="textSecondary" gutterBottom>
                      Classes Monitored
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="success.main">
                    {Object.keys(analyticsData.classStats || {}).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active classes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                    <Typography color="textSecondary" gutterBottom>
                      Weekly Trend
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="info.main">
                    {(analyticsData.weeklyTrend || []).length > 0 
                      ? Math.round((analyticsData.weeklyTrend || []).reduce((sum, week) => sum + (week?.percentage || 0), 0) / (analyticsData.weeklyTrend || []).length)
                      : 0}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Last 4 weeks
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssessmentIcon color="warning" sx={{ mr: 1 }} />
                    <Typography color="textSecondary" gutterBottom>
                      Top Absentee
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="warning.main">
                    {(analyticsData.topAbsentees || []).length > 0 ? (analyticsData.topAbsentees || [])[0]?.absences || 0 : 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Most absences
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Class-wise Analytics */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Class-wise Attendance
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {Object.entries(analyticsData.classStats || {}).map(([className, stats]) => (
                    <Box key={className} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {className}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {stats.percentage}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stats.percentage}
                        color={stats.percentage >= 80 ? 'success' : stats.percentage >= 60 ? 'warning' : 'error'}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          Present: {stats.present} | Absent: {stats.absent}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Total: {stats.total}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Weekly Attendance Trend
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {(analyticsData.weeklyTrend || []).map((week, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {week.week}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {week.percentage}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={week.percentage}
                        color={week.percentage >= 80 ? 'success' : week.percentage >= 60 ? 'warning' : 'error'}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                          Present: {week.present} | Absent: {week.absent}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Days: {week.days}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Top Absentees and Daily Stats */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Top Absentees ({getCurrentTerm()})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    {(analyticsData.topAbsentees || []).slice(0, 5).map((student, index) => (
                      <ListItem key={student._id} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'error.light' }}>
                            {index + 1}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={`${student.lastname} ${student.othernames}`}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                Class: {student.class_} | ID: {student.ID}
                              </Typography>
                              <Typography variant="caption" color="error">
                                Absences: {student.absences} | Rate: {student.attendanceRate}%
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Recent Daily Attendance
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    {(analyticsData.dailyStats || []).slice(-7).reverse().map((day, index) => (
                      <ListItem key={day.date} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <Chip
                            label={new Date(day.date).getDate()}
                            size="small"
                            color={day.percentage >= 80 ? 'success' : day.percentage >= 60 ? 'warning' : 'error'}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={new Date(day.date).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                Present: {day.present} | Absent: {day.absent} | Unmarked: {day.unmarked}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Rate: {day.percentage}%
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Term-wise Statistics */}
          {Object.keys(analyticsData.termStats).length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <PieChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Term-wise Attendance Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {Object.entries(analyticsData.termStats).map(([term, stats]) => (
                    <Grid item xs={12} sm={6} md={4} key={term}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h6" color="primary" gutterBottom>
                          {term}
                        </Typography>
                        <Typography variant="h4" color="success.main" gutterBottom>
                          {stats.percentage}%
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Present: {stats.present} | Absent: {stats.absent}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Total Records: {stats.totalRecords} | Days: {stats.totalDays}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Attendance Patterns by Day of Week */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <BarChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Attendance Patterns by Day of Week
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                  {Object.entries(analyticsData.attendancePatterns || {}).map(([day, stats]) => {
                  const total = (stats?.present || 0) + (stats?.absent || 0);
                  const percentage = total > 0 ? Math.round(((stats?.present || 0) / total) * 100) : 0;
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} key={day}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                          {day}
                        </Typography>
                        <Typography variant="h5" color="primary" gutterBottom>
                          {percentage}%
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Present: {stats?.present || 0} | Absent: {stats?.absent || 0}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          color={percentage >= 80 ? 'success' : percentage >= 60 ? 'warning' : 'error'}
                          sx={{ mt: 1, height: 6, borderRadius: 3 }}
                        />
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Snackbar for messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={messageType}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
